import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building2, CreditCard, Users, FileText, RefreshCw,
  Plus, Trash2, Pencil, Save, X, Globe, Phone, Mail
} from 'lucide-react';

function EditableField({ label, value, field, editing, onChange, type = "text", sensitive }) {
  if (sensitive && !editing) return (
    <div>
      <span className="text-xs text-[#64748B] block mb-1">{label}</span>
      <span className="text-sm text-[#1A3A5C]">{'••••••••'}</span>
    </div>
  );
  return (
    <div>
      <span className="text-xs text-[#64748B] block mb-1">{label}</span>
      {editing ? (
        <Input value={value || ''} onChange={e => onChange(field, e.target.value)}
          type={type} className="h-8 text-sm" data-testid={`field-${field}`} />
      ) : (
        <span className="text-sm text-[#1A3A5C]">{value || '—'}</span>
      )}
    </div>
  );
}

export default function CompanyPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'org_admin'].includes(user?.role);
  const [profile, setProfile] = useState(null);
  const [subs, setSubs] = useState([]);
  const [people, setPeople] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [newPerson, setNewPerson] = useState({ full_name: '', email: '', role: 'contact', phone: '', notes: '' });
  const [newSub, setNewSub] = useState({ service_name: '', plan: '', cost: '', billing_cycle: 'mensile', status: 'attivo' });
  const [newContract, setNewContract] = useState({ contract_type: '', counterparty: '', start_date: '', end_date: '', annual_amount: '', status: 'attivo', notes: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, ppl, c] = await Promise.all([
        api.get('/company/profile'), api.get('/company/subscriptions'),
        api.get('/company/people'), api.get('/company/contracts')
      ]);
      setProfile(p.data); setDraft(p.data);
      setSubs(s.data); setPeople(ppl.data); setContracts(c.data);
    } catch { toast.error('Errore caricamento dati azienda'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleChange = (field, value) => {
    setDraft(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return { ...prev, [parent]: { ...prev[parent], [child]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const saveProfile = async () => {
    try {
      const res = await api.patch('/company/profile', draft);
      setProfile(res.data); setEditing(false);
      toast.success('Profilo aggiornato');
    } catch { toast.error('Errore salvataggio'); }
  };

  const addPerson = async () => {
    try {
      await api.post('/company/people', newPerson);
      setShowAddPerson(false); setNewPerson({ full_name: '', email: '', role: 'contact', phone: '', notes: '' });
      const r = await api.get('/company/people'); setPeople(r.data);
      toast.success('Persona aggiunta');
    } catch { toast.error('Errore'); }
  };

  const deletePerson = async (id) => {
    try {
      await api.delete(`/company/people/${id}`);
      setPeople(prev => prev.filter(p => p.id !== id));
      toast.success('Persona rimossa');
    } catch { toast.error('Errore'); }
  };

  const addSub = async () => {
    try {
      await api.post('/company/subscriptions', newSub);
      setShowAddSub(false); setNewSub({ service_name: '', plan: '', cost: '', billing_cycle: 'mensile', status: 'attivo' });
      const r = await api.get('/company/subscriptions'); setSubs(r.data);
      toast.success('Abbonamento aggiunto');
    } catch { toast.error('Errore'); }
  };

  const deleteSub = async (id) => {
    try {
      await api.delete(`/company/subscriptions/${id}`);
      setSubs(prev => prev.filter(s => s.id !== id));
      toast.success('Abbonamento rimosso');
    } catch { toast.error('Errore'); }
  };

  const addContract = async () => {
    try {
      await api.post('/company/contracts', newContract);
      setShowAddContract(false); setNewContract({ contract_type: '', counterparty: '', start_date: '', end_date: '', annual_amount: '', status: 'attivo', notes: '' });
      const r = await api.get('/company/contracts'); setContracts(r.data);
      toast.success('Contratto aggiunto');
    } catch { toast.error('Errore'); }
  };

  const deleteContract = async (id) => {
    try {
      await api.delete(`/company/contracts/${id}`);
      setContracts(prev => prev.filter(c => c.id !== id));
      toast.success('Contratto rimosso');
    } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-[#2E75B6]" /></div>;

  return (
    <div className="space-y-6" data-testid="company-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A3A5C]">Dati Azienda</h1>
          <p className="text-sm text-[#64748B] mt-1">Dati aziendali centralizzati</p>
        </div>
        {isAdmin && !editing && (
          <Button onClick={() => setEditing(true)} variant="outline" size="sm" data-testid="edit-profile-btn">
            <Pencil className="w-4 h-4 mr-2" /> Modifica
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button onClick={saveProfile} size="sm" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" /> Salva
            </Button>
            <Button onClick={() => { setEditing(false); setDraft(profile); }} variant="ghost" size="sm">
              <X className="w-4 h-4 mr-2" /> Annulla
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="anagrafica" className="w-full">
        <TabsList className="grid w-full grid-cols-5" data-testid="company-tabs">
          <TabsTrigger value="anagrafica"><Building2 className="w-4 h-4 mr-1.5" />Anagrafica</TabsTrigger>
          <TabsTrigger value="fatturazione"><CreditCard className="w-4 h-4 mr-1.5" />Fatturazione</TabsTrigger>
          <TabsTrigger value="persone"><Users className="w-4 h-4 mr-1.5" />Persone</TabsTrigger>
          <TabsTrigger value="abbonamenti"><Globe className="w-4 h-4 mr-1.5" />Abbonamenti</TabsTrigger>
          <TabsTrigger value="contratti"><FileText className="w-4 h-4 mr-1.5" />Contratti</TabsTrigger>
        </TabsList>

        <TabsContent value="anagrafica">
          <Card>
            <CardHeader><CardTitle className="text-base">Anagrafica Azienda</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <EditableField label="Nome" value={draft?.org_name} field="org_name" editing={editing} onChange={handleChange} />
              <EditableField label="Ragione Sociale" value={draft?.legal_name} field="legal_name" editing={editing} onChange={handleChange} />
              <EditableField label="Partita IVA" value={draft?.vat_number} field="vat_number" editing={editing} onChange={handleChange} />
              <EditableField label="Forma Giuridica" value={draft?.legal_form} field="legal_form" editing={editing} onChange={handleChange} />
              <EditableField label="Cantone" value={draft?.canton} field="canton" editing={editing} onChange={handleChange} />
              <EditableField label="Data Fondazione" value={draft?.founding_date} field="founding_date" editing={editing} onChange={handleChange} type="date" />
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#64748B]" />
                <EditableField label="Telefono" value={draft?.phone} field="phone" editing={editing} onChange={handleChange} />
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#64748B]" />
                <EditableField label="Email" value={draft?.email_main} field="email_main" editing={editing} onChange={handleChange} />
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#64748B]" />
                <EditableField label="Sito Web" value={draft?.website} field="website" editing={editing} onChange={handleChange} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fatturazione">
          <Card>
            <CardHeader><CardTitle className="text-base">Dati Fatturazione</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <EditableField label="Nome Fatturazione" value={draft?.billing?.billing_name} field="billing.billing_name" editing={editing} onChange={handleChange} />
              <EditableField label="IBAN" value={draft?.billing?.iban} field="billing.iban" editing={editing} onChange={handleChange} sensitive={!isAdmin} />
              <EditableField label="BIC" value={draft?.billing?.bic} field="billing.bic" editing={editing} onChange={handleChange} sensitive={!isAdmin} />
              <EditableField label="Banca" value={draft?.billing?.bank_name} field="billing.bank_name" editing={editing} onChange={handleChange} />
              <EditableField label="Termini Pagamento (gg)" value={draft?.billing?.payment_terms_days} field="billing.payment_terms_days" editing={editing} onChange={handleChange} type="number" />
              <EditableField label="Prefisso Fattura" value={draft?.billing?.invoice_prefix} field="billing.invoice_prefix" editing={editing} onChange={handleChange} />
              <EditableField label="IVA %" value={draft?.billing?.vat_rate} field="billing.vat_rate" editing={editing} onChange={handleChange} type="number" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="persone">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Persone</CardTitle>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setShowAddPerson(true)} data-testid="add-person-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddPerson && (
                <div className="bg-[#F5F5F5] p-4 rounded-lg mb-4 space-y-2" data-testid="add-person-form">
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Nome completo" value={newPerson.full_name} onChange={e => setNewPerson(p => ({ ...p, full_name: e.target.value }))} />
                    <Input placeholder="Email" value={newPerson.email} onChange={e => setNewPerson(p => ({ ...p, email: e.target.value }))} />
                    <select className="border rounded-md px-3 text-sm" value={newPerson.role} onChange={e => setNewPerson(p => ({ ...p, role: e.target.value }))}>
                      <option value="founder">Founder</option>
                      <option value="admin">Admin</option>
                      <option value="collaborator">Collaboratore</option>
                      <option value="advisor">Advisor</option>
                      <option value="contact">Contatto</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addPerson}>Salva</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddPerson(false)}>Annulla</Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {people.length === 0 && <p className="text-sm text-[#64748B]">Nessuna persona registrata</p>}
                {people.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-white border rounded-lg" data-testid={`person-${p.id}`}>
                    <div>
                      <span className="font-medium text-sm text-[#1A3A5C]">{p.full_name}</span>
                      <span className="text-xs text-[#64748B] ml-2">{p.role}</span>
                      {p.email && <span className="text-xs text-[#64748B] ml-2">{p.email}</span>}
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => deletePerson(p.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="abbonamenti">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Abbonamenti e Servizi</CardTitle>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setShowAddSub(true)} data-testid="add-sub-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddSub && (
                <div className="bg-[#F5F5F5] p-4 rounded-lg mb-4 space-y-2" data-testid="add-sub-form">
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Servizio" value={newSub.service_name} onChange={e => setNewSub(s => ({ ...s, service_name: e.target.value }))} />
                    <Input placeholder="Piano" value={newSub.plan} onChange={e => setNewSub(s => ({ ...s, plan: e.target.value }))} />
                    <Input placeholder="Costo" value={newSub.cost} onChange={e => setNewSub(s => ({ ...s, cost: e.target.value }))} />
                    <select className="border rounded-md px-3 text-sm" value={newSub.billing_cycle} onChange={e => setNewSub(s => ({ ...s, billing_cycle: e.target.value }))}>
                      <option value="mensile">Mensile</option>
                      <option value="annuale">Annuale</option>
                      <option value="-">-</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addSub}>Salva</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddSub(false)}>Annulla</Button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#64748B] border-b">
                      <th className="pb-2">Servizio</th><th className="pb-2">Piano</th>
                      <th className="pb-2">Costo</th><th className="pb-2">Ciclo</th>
                      <th className="pb-2">Stato</th>{isAdmin && <th className="pb-2"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map(s => (
                      <tr key={s.id} className="border-b last:border-0" data-testid={`sub-${s.id}`}>
                        <td className="py-2 font-medium text-[#1A3A5C]">{s.service_name}</td>
                        <td className="py-2">{s.plan}</td>
                        <td className="py-2">{s.cost}</td>
                        <td className="py-2">{s.billing_cycle}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${s.status === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {s.status}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="py-2">
                            <Button variant="ghost" size="sm" onClick={() => deleteSub(s.id)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contratti">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Contratti e Accordi</CardTitle>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setShowAddContract(true)} data-testid="add-contract-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddContract && (
                <div className="bg-[#F5F5F5] p-4 rounded-lg mb-4 space-y-2" data-testid="add-contract-form">
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Tipo contratto" value={newContract.contract_type} onChange={e => setNewContract(c => ({ ...c, contract_type: e.target.value }))} />
                    <Input placeholder="Controparte" value={newContract.counterparty} onChange={e => setNewContract(c => ({ ...c, counterparty: e.target.value }))} />
                    <Input placeholder="Importo annuo" value={newContract.annual_amount} onChange={e => setNewContract(c => ({ ...c, annual_amount: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="date" value={newContract.start_date} onChange={e => setNewContract(c => ({ ...c, start_date: e.target.value }))} />
                    <Input type="date" value={newContract.end_date} onChange={e => setNewContract(c => ({ ...c, end_date: e.target.value }))} />
                    <Input placeholder="Note" value={newContract.notes} onChange={e => setNewContract(c => ({ ...c, notes: e.target.value }))} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addContract}>Salva</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddContract(false)}>Annulla</Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {contracts.length === 0 && <p className="text-sm text-[#64748B]">Nessun contratto registrato</p>}
                {contracts.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 bg-white border rounded-lg" data-testid={`contract-${c.id}`}>
                    <div>
                      <span className="font-medium text-sm text-[#1A3A5C]">{c.contract_type}</span>
                      <span className="text-xs text-[#64748B] ml-2">— {c.counterparty}</span>
                      {c.end_date && <span className="text-xs text-[#64748B] ml-2">scadenza: {c.end_date}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${c.status === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {c.status}
                      </span>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => deleteContract(c.id)}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
